import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { createApp, updateApp, saveAppPreviewHtml, getLaunchConfig } from '../services/appRegistry';
import { developAppStream, DevelopResponse, StreamChunk } from '../services/godApi';
import {
  SpaceBackground,
  GlassCard,
  AtomIcon,
  ChevronLeft,
  Smartphone,
  Plus,
  FileText,
  Link,
  Upload,
  X,
  ArrowUp,
  Edit,
} from '../components/shared';

// Knowledge item type
interface KnowledgeItem {
  id: number;
  type: 'file' | 'link' | 'image' | 'video';
  name: string;
  url?: string;
}

// Plus menu for adding knowledge
const PlusMenu = ({
  isOpen,
  onClose,
  onAddFile,
  onAddLink,
  onEditSettings
}: {
  isOpen: boolean;
  onClose: () => void;
  onAddFile: () => void;
  onAddLink: () => void;
  onEditSettings?: () => void;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute bottom-16 left-0 w-56 bg-black/95 backdrop-blur-xl border border-cyan-400/30 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(34,211,238,0.3)] z-50"
    >
      <button
        onClick={() => { onAddFile(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all"
      >
        <Upload className="w-4 h-4" />
        <span>Upload files, photos, videos</span>
      </button>
      <button
        onClick={() => { onAddLink(); onClose(); }}
        className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all border-t border-cyan-400/10"
      >
        <Link className="w-4 h-4" />
        <span>Add link from internet</span>
      </button>
      {onEditSettings && (
        <button
          onClick={() => { onEditSettings(); onClose(); }}
          className="w-full text-left px-4 py-3 text-sm text-cyan-400/90 hover:bg-cyan-400/10 hover:text-cyan-300 flex items-center space-x-3 transition-all border-t border-cyan-400/10"
        >
          <Edit className="w-4 h-4" />
          <span>Edit app settings</span>
        </button>
      )}
    </div>
  );
};

// Link input modal
const LinkInputModal = ({
  isOpen,
  onClose,
  onAdd
}: {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (url: string) => void;
}) => {
  const [url, setUrl] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 bg-black/95 border border-cyan-400/30 rounded-2xl p-6 shadow-[0_0_50px_rgba(34,211,238,0.3)]">
        <h3 className="text-lg font-semibold text-cyan-400 mb-4">Add Link</h3>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="w-full bg-cyan-400/5 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 outline-none focus:border-cyan-400/60 mb-4"
        />
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-cyan-400/70 hover:text-cyan-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { onAdd(url); setUrl(''); onClose(); }}
            disabled={!url.trim()}
            className={`px-4 py-2 rounded-xl font-semibold transition-all ${url.trim() ? 'bg-cyan-400 text-black' : 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'}`}
          >
            Add Link
          </button>
        </div>
      </div>
    </div>
  );
};

// Summary bar item - clickable to edit
const SummaryItem = ({
  label,
  value,
  onClick
}: {
  label: string;
  value: string;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className="flex items-start space-x-2 text-left group hover:bg-cyan-400/5 p-2 rounded-lg transition-all -mx-2"
  >
    <span className="text-cyan-400/50 text-xs font-semibold uppercase w-20 md:w-24 pt-0.5 shrink-0">{label}:</span>
    <span className="text-cyan-300 flex-1 line-clamp-1 text-sm">{value || 'Not set'}</span>
    <Edit className="w-3 h-3 text-cyan-400/30 group-hover:text-cyan-400 opacity-0 group-hover:opacity-100 transition-all shrink-0 mt-0.5" />
  </button>
);

export default function CreateAppScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const [phase, setPhase] = useState<'form' | 'building'>(((location.state as any)?.editApp) ? 'building' : 'form');
  const [isSaving, setIsSaving] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const lastSavedRef = useRef<{ name: string; description: string; instructions: string; goal: string; previewHtml: string }>({ name: '', description: '', instructions: '', goal: '', previewHtml: '' });

  // Check for prefill from navigation state (from proactive action or edit mode)
  const prefillState = location.state as {
    prefill?: boolean; context?: string; type?: string;
    editApp?: { app_id?: string; name: string; description?: string; instructions?: string; goal?: string; metadata?: Record<string, unknown> };
  } | null;
  const editApp = prefillState?.editApp;
  const isEditMode = !!editApp;

  // Restore knowledge items from editApp metadata
  const initialKnowledge: KnowledgeItem[] = (() => {
    const metaKnowledge = editApp?.metadata?.knowledge;
    if (Array.isArray(metaKnowledge)) {
      return metaKnowledge.map((k: Record<string, unknown>, i: number) => ({
        id: Date.now() + i,
        type: (k.type as KnowledgeItem['type']) || 'file',
        name: (k.name as string) || '',
        url: k.url as string | undefined,
      }));
    }
    return [];
  })();

  // Form state - prefill from navigation if available, or from edit app
  const [appName, setAppName] = useState(editApp?.name || '');
  const [description, setDescription] = useState(editApp?.description || '');
  const [instructions, setInstructions] = useState((editApp?.metadata?.instructions as string) || editApp?.instructions || '');
  const [goal, setGoal] = useState((editApp?.metadata?.goal as string) || editApp?.goal || prefillState?.context || '');
  const [knowledge, setKnowledge] = useState<KnowledgeItem[]>(initialKnowledge);

  // Building phase state
  const [inputText, setInputText] = useState('');
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  const [conversation, setConversation] = useState<{ role: string; message: string }[]>([]);

  // Load preview_html and initial conversation when opening an existing app
  useEffect(() => {
    if (isEditMode && editApp?.app_id) {
      getLaunchConfig(editApp.app_id).then(config => {
        if (config.preview_html) {
          setPreviewHtml(config.preview_html);
          setAppPreviewState(prev => ({ ...prev, hasContent: true, hasFooter: true }));
        }
      }).catch(err => {
        console.error('[CreateApp] Failed to load draft preview html', err);
      });

      setConversation(prev => {
        if (prev.length === 0) {
          return [{
            role: 'assistant',
            message: `Welcome back to building "${editApp.name}". The preview shows the current state. Feel free to describe what you want me to do next.`
          }];
        }
        return prev;
      });
    }
  }, [isEditMode, editApp?.app_id, editApp?.name]);

  // App preview state (simulates app being built)
  const [_appPreviewState, setAppPreviewState] = useState({
    hasHeader: true,
    hasContent: false,
    hasFooter: false,
    primaryColor: 'cyan',
    features: [] as string[]
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  const handleAddFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const type = file.type.startsWith('image/') ? 'image' :
          file.type.startsWith('video/') ? 'video' : 'file';
        setKnowledge(prev => [...prev, {
          id: Date.now() + Math.random(),
          type,
          name: file.name
        }]);
      });
    }
  };

  const handleAddLink = (url: string) => {
    if (url.trim()) {
      setKnowledge(prev => [...prev, {
        id: Date.now(),
        type: 'link',
        name: url,
        url
      }]);
    }
  };

  const handleRemoveKnowledge = (id: number) => {
    setKnowledge(knowledge.filter(k => k.id !== id));
  };

  const handleCreate = async () => {
    if (!appName || !description || !user?.uid) return;

    setIsSaving(true);
    try {
      // Save app to appRegistry API
      const createdApp = await createApp(user.uid, {
        name: appName,
        icon: 'smartphone', // Default icon
        description: description,
        metadata: {
          instructions,
          goal,
          knowledge: knowledge.map(k => ({ type: k.type, name: k.name, url: k.url }))
        }
      });
      // Store app_id for saving preview_html after build
      if (createdApp?.app_id) {
        setCreatedAppId(createdApp.app_id);
      }

      // Start building phase
      const initialMessages: { role: string; message: string }[] = [
        {
          role: 'assistant',
          message: `Great! I'm ready to help you build "${appName}". Based on your description and instructions, I'll start creating the app. The preview on the right will update as we build. What would you like me to create first?`
        }
      ];

      setConversation(initialMessages);
      setAppPreviewState(prev => ({ ...prev, hasContent: true }));
      setPhase('building');
    } catch (error) {
      console.error('[CreateAppScreen] Error creating app:', error);
      alert('Failed to create app. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const [isPublishing, setIsPublishing] = useState(false);

  const handleSaveDraft = async () => {
    const targetAppId = editApp?.app_id || createdAppId;
    if (!targetAppId) {
      // No app_id means we haven't persisted yet — just navigate back
      navigate('/apps');
      return;
    }
    try {
      setIsSaving(true);
      await updateApp(targetAppId, {
        name: appName,
        description,
        metadata: { instructions, goal, status: 'draft', knowledge: knowledge.map(k => ({ type: k.type, name: k.name, url: k.url })) }
      });
      alert('Draft saved successfully.');
      navigate('/apps');
    } catch (error) {
      console.error('[CreateAppScreen] Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    const targetAppId = editApp?.app_id || createdAppId;
    if (!targetAppId) {
      alert('App published! You can find it in My Apps.');
      navigate('/apps');
      return;
    }
    try {
      setIsPublishing(true);
      // Update app metadata + status
      await updateApp(targetAppId, {
        name: appName,
        description,
        metadata: { instructions, goal, status: 'published' }
      });
      // Save the built preview_html so the app can be used after publishing
      if (previewHtml) {
        await saveAppPreviewHtml(targetAppId, previewHtml);
      }
      alert('App published successfully!');
      navigate('/apps');
    } catch (error) {
      console.error('[CreateAppScreen] Error publishing app:', error);
      alert('Failed to publish app. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const [isBuilding, setIsBuilding] = useState(false);
  const [buildStatus, setBuildStatus] = useState('');
  const [generatedCode, setGeneratedCode] = useState<string[]>([]);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [createdAppId, setCreatedAppId] = useState<string>(editApp?.app_id || '');
  const [generatedFiles, setGeneratedFiles] = useState<{ path: string; content: string }[]>([]);

  // Auto-save draft every 30 seconds when there are unsaved changes
  useEffect(() => {
    const targetAppId = editApp?.app_id || createdAppId;
    if (!targetAppId || phase !== 'building') return;

    const interval = setInterval(async () => {
      const current = { name: appName, description, instructions, goal, previewHtml };
      const last = lastSavedRef.current;
      const hasChanges =
        current.name !== last.name ||
        current.description !== last.description ||
        current.instructions !== last.instructions ||
        current.goal !== last.goal ||
        current.previewHtml !== last.previewHtml;

      if (!hasChanges || isSaving) return;

      try {
        await updateApp(targetAppId, {
          name: appName,
          description,
          metadata: {
            instructions,
            goal,
            status: 'draft',
            knowledge: knowledge.map(k => ({ type: k.type, name: k.name, url: k.url }))
          }
        });
        if (previewHtml) {
          await saveAppPreviewHtml(targetAppId, previewHtml);
        }
        lastSavedRef.current = { ...current };
        setDraftSavedAt(new Date());
      } catch (err) {
        console.error('[CreateApp] Auto-save failed:', err);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [phase, appName, description, instructions, goal, previewHtml, knowledge, editApp?.app_id, createdAppId, isSaving]);

  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || isBuilding) return;

    const userMessage = inputText.trim();
    setConversation(prev => [...prev, { role: 'user', message: userMessage }]);
    setInputText('');
    setIsBuilding(true);
    setBuildStatus('Thinking...');

    try {
      // Build requirements from app details + user message
      const requirements = `
App Name: ${appName}
Description: ${description}
${instructions ? `Instructions: ${instructions}` : ''}
${goal ? `Goal: ${goal}` : ''}
User Request: ${userMessage}
      `.trim();

      // Stream development updates from God Agent
      await developAppStream(
        {
          requirements,
          project_name: appName,
          knowledge_base: knowledge.map(k => ({ type: k.type, name: k.name, url: k.url }))
        },
        // onChunk - handle streaming updates
        (chunk: StreamChunk) => {
          switch (chunk.type) {
            case 'thinking':
              setBuildStatus(`Thinking: ${chunk.data.substring(0, 50)}...`);
              break;
            case 'code':
              if (chunk.file_path) {
                setGeneratedFiles(prev => {
                  const existing = prev.findIndex(f => f.path === chunk.file_path);
                  if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = { path: chunk.file_path!, content: chunk.data };
                    return updated;
                  }
                  return [...prev, { path: chunk.file_path!, content: chunk.data }];
                });
              }
              setGeneratedCode(prev => [...prev, chunk.data]);
              setBuildStatus('Writing code...');
              break;
            case 'feature':
              setAppPreviewState(prev => ({
                ...prev,
                features: [...prev.features, chunk.data]
              }));
              break;
            case 'preview':
              setPreviewHtml(chunk.data);
              setBuildStatus('Updating preview...');
              break;
            case 'status':
              setBuildStatus(chunk.data);
              break;
          }
        },
        // onComplete
        (result: DevelopResponse) => {
          setIsBuilding(false);
          setBuildStatus('');

          const previewHtmlFromBackend = result?.result?.preview_html || '';
          const filesFromBackend = result?.result?.files || [];
          const featuresFromBackend = result?.result?.features || [];
          const hasBackendArtifact = Boolean(previewHtmlFromBackend) || filesFromBackend.length > 0;

          // Capture real preview HTML and files from result
          if (previewHtmlFromBackend) {
            setPreviewHtml(previewHtmlFromBackend);
            // Persist preview_html to app registry for re-launch
            if (createdAppId) {
              saveAppPreviewHtml(createdAppId, previewHtmlFromBackend);
            }
          }
          if (filesFromBackend.length) {
            setGeneratedFiles(filesFromBackend);
          }

          // Update preview state only from backend-returned artifacts
          if (featuresFromBackend.length) {
            setAppPreviewState(prev => ({
              ...prev,
              features: [...new Set([...prev.features, ...featuresFromBackend])],
              hasFooter: true,
              hasContent: true
            }));
          }
          if (previewHtmlFromBackend || filesFromBackend.length) {
            setAppPreviewState(prev => ({
              ...prev,
              hasContent: true,
              hasFooter: prev.hasFooter || featuresFromBackend.length > 0
            }));
          }

          // Add AI response
          const teamContributions = result.team_contributions
            ? Object.entries(result.team_contributions)
              .map(([name, work]) => `• ${name}: ${work}`)
              .join('\n')
            : '';

          setConversation(prev => [...prev, {
            role: 'assistant',
            message: hasBackendArtifact
              ? `Backend-confirmed update received.${teamContributions ? '\n\nTeam contributions:\n' + teamContributions : ''}${previewHtmlFromBackend ? '\n\nPreview HTML returned by backend.' : ''}${filesFromBackend.length ? `\nCode files returned: ${filesFromBackend.length}.` : ''}\n\nWhat else would you like to modify?`
              : `Build completed but no backend artifact was returned.${teamContributions ? '\n\nTeam contributions:\n' + teamContributions : ''}\n\nI am not claiming the app changed. Please retry or inspect the backend response.`
          }]);
        },
        // onError
        (error: Error) => {
          setIsBuilding(false);
          setBuildStatus('');
          console.error('[CreateApp] Build error:', error);

          setConversation(prev => [...prev, {
            role: 'assistant',
            message: `Build failed before backend confirmation: ${error.message}. No app update has been claimed.`
          }]);
        }
      );
    } catch (error) {
      console.error('[CreateApp] Error:', error);
      setIsBuilding(false);
      setBuildStatus('');

      const message = error instanceof Error ? error.message : String(error);
      setConversation(prev => [...prev, {
        role: 'assistant',
        message: `Build request failed: ${message}. No backend-confirmed change was applied.`
      }]);
    }
  }, [inputText, isBuilding, appName, description, instructions, goal, knowledge]);

  const handleEditField = () => {
    setPhase('form');
  };

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [conversation]);

  // Navigate home when clicking logo
  const handleLogoClick = () => {
    navigate('/');
  };

  // Live Preview Component - Shows app being built (for desktop and mobile modal)
  const LivePreviewPanel = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className={`${isMobile ? 'h-full' : 'w-full h-full'} flex flex-col bg-gradient-to-br from-black to-cyan-950/20`}>
      {/* Preview Header */}
      <header className="p-4 flex items-center justify-between border-b border-cyan-400/10">
        <h2 className="text-lg font-semibold text-cyan-400/80">Live Preview</h2>
        <div className="flex items-center space-x-2">
          {generatedFiles.length > 0 && (
            <button
              onClick={() => {
                const blob = new Blob(
                  [generatedFiles.map(f => `// === ${f.path} ===\n${f.content}`).join('\n\n')],
                  { type: 'text/plain' }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${appName || 'app'}-source.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className='px-3 py-1 text-xs bg-cyan-400/10 border border-cyan-400/30 rounded-full text-cyan-400 hover:bg-cyan-400/20'
            >
              Download Code
            </button>
          )}
          {isMobile && (
            <button
              onClick={() => setShowMobilePreview(false)}
              className="p-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 mr-2"
            >
              <X className="w-4 h-4 text-cyan-400" />
            </button>
          )}
          {draftSavedAt && (
            <span className="text-green-400/70 text-xs hidden sm:inline animate-fade-in">
              Draft saved {draftSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <span className="text-cyan-400/50 text-xs hidden sm:inline">Auto-updating</span>
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
        </div>
      </header>

      {/* Preview Content */}
      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        {previewHtml ? (
          <iframe
            srcDoc={previewHtml}
            className='w-full h-full min-h-[500px] rounded-xl border border-cyan-400/20 bg-white'
            sandbox='allow-scripts allow-same-origin'
            title='App Preview'
            style={{ background: 'white' }}
          />
        ) : (
          <div className='bg-white/5 backdrop-blur-xl border border-cyan-400/20 rounded-2xl overflow-hidden min-h-[400px] md:min-h-[500px] flex flex-col items-center justify-center'>
            <Smartphone className='w-16 h-16 text-cyan-400/30 mb-4' />
            <p className='text-cyan-400/50 text-sm'>Your app preview will appear here</p>
            <p className='text-cyan-400/30 text-xs mt-1'>Start building to see live updates</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {phase === 'building' && (
        <div className="p-4 border-t border-cyan-400/10 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="px-6 py-3 rounded-xl bg-cyan-400/5 border border-cyan-400/30 text-cyan-400/80 hover:bg-cyan-400/10 hover:text-cyan-300 transition-all font-semibold text-sm md:text-base disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={handlePublish}
            disabled={isPublishing}
            className="px-6 py-3 rounded-xl font-semibold transition-all bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_20px_rgba(34,211,238,0.4)] text-sm md:text-base disabled:opacity-50"
          >
            {isPublishing ? 'Publishing...' : 'Publish App'}
          </button>
        </div>
      )}
    </div>
  );

  // Form Phase - Split screen with form on left, preview on right (responsive)
  if (phase === 'form') {
    return (
      <div className="fixed inset-0 z-[70] bg-black font-sans">
        <SpaceBackground />

        <div className="relative z-10 flex flex-col lg:flex-row h-full">
          {/* Left Side - Form (full width on mobile, half on desktop) */}
          <div className="flex-1 lg:w-1/2 flex flex-col lg:border-r border-cyan-400/20">
            {/* Header */}
            <header className="p-4 md:p-6 flex items-center border-b border-cyan-400/10">
              <button
                onClick={() => navigate('/apps')}
                className="p-2 md:p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-4 md:mr-6"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
              </button>

              <button onClick={handleLogoClick} className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity">
                <AtomIcon className="w-8 h-8 md:w-10 md:h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
                <h1 className="text-xl md:text-2xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] font-sans">
                  {isEditMode ? 'Edit App' : 'Create New App'}
                </h1>
              </button>

              <button onClick={handleLogoClick} className="ml-auto hover:scale-105 transition-transform hidden md:block">
                <AtomIcon className="w-12 h-12 text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)] animate-spin-slow" />
              </button>
            </header>

            {/* Form Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="space-y-5 md:space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Name your app</label>
                  <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="My Amazing App"
                    className="w-full bg-cyan-400/5 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 outline-none focus:border-cyan-400/60 focus:shadow-[0_0_20px_rgba(34,211,238,0.2)] transition-all text-base"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Add a short description about your app</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what your app does..."
                    rows={3}
                    className="w-full bg-cyan-400/5 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 outline-none focus:border-cyan-400/60 focus:shadow-[0_0_20px_rgba(34,211,238,0.2)] transition-all resize-none text-base"
                  />
                </div>

                {/* Instructions */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Instructions</label>
                  <p className="text-cyan-400/50 text-xs mb-2">What does this app do? How does it behave? What should it avoid doing?</p>
                  <textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Provide detailed instructions for your app..."
                    rows={4}
                    className="w-full bg-cyan-400/5 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 outline-none focus:border-cyan-400/60 focus:shadow-[0_0_20px_rgba(34,211,238,0.2)] transition-all resize-none text-base"
                  />
                </div>

                {/* Knowledge */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Knowledge</label>
                  <p className="text-cyan-400/50 text-xs mb-3">Upload files, images, videos, or add links as reference material</p>

                  {knowledge.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {knowledge.map(item => (
                        <div key={item.id} className="flex items-center space-x-2 bg-cyan-400/10 border border-cyan-400/20 rounded-lg px-3 py-2">
                          {item.type === 'link' ? <Link className="w-4 h-4 text-cyan-400" /> : <FileText className="w-4 h-4 text-cyan-400" />}
                          <span className="text-cyan-300 text-sm truncate max-w-[120px] md:max-w-[150px]">{item.name}</span>
                          <button onClick={() => handleRemoveKnowledge(item.id)} className="text-cyan-400/50 hover:text-red-400 transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleAddFile}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 rounded-xl text-cyan-400 hover:bg-cyan-400/20 transition-all"
                    >
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">Upload files</span>
                    </button>
                    <button
                      onClick={() => setLinkModalOpen(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-400/10 border border-cyan-400/30 rounded-xl text-cyan-400 hover:bg-cyan-400/20 transition-all"
                    >
                      <Link className="w-4 h-4" />
                      <span className="text-sm">Add link</span>
                    </button>
                  </div>
                </div>

                {/* Goal / Purpose */}
                <div>
                  <label className="block text-cyan-400/80 text-sm font-semibold mb-2">Goal / Purpose of the App</label>
                  <textarea
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="What is the overall goal or purpose of this app?"
                    rows={3}
                    className="w-full bg-cyan-400/5 border border-cyan-400/30 rounded-xl px-4 py-3 text-cyan-300 placeholder-cyan-400/40 outline-none focus:border-cyan-400/60 focus:shadow-[0_0_20px_rgba(34,211,238,0.2)] transition-all resize-none text-base"
                  />
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="p-4 md:p-6 border-t border-cyan-400/10 bg-black/50 backdrop-blur-xl">
              {/* Mobile: Preview Button */}
              <button
                onClick={() => setShowMobilePreview(true)}
                className="w-full py-3 mb-3 rounded-xl font-semibold text-base transition-all bg-cyan-400/10 text-cyan-400 border border-cyan-400/30 hover:bg-cyan-400/20 lg:hidden"
              >
                Preview App
              </button>

              {/* Create Button */}
              <button
                onClick={handleCreate}
                disabled={!appName || !description || isSaving}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${appName && description && !isSaving
                  ? 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:shadow-[0_0_40px_rgba(34,211,238,0.7)]'
                  : 'bg-cyan-400/20 text-cyan-400/40 cursor-not-allowed'
                  }`}
              >
                {isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create App')}
              </button>
            </div>
          </div>

          {/* Right Side - Preview (hidden on mobile, shown on lg+) */}
          <div className="hidden lg:flex lg:w-1/2">
            <LivePreviewPanel />
          </div>
        </div>

        {/* Mobile Preview Modal */}
        {showMobilePreview && (
          <div className="fixed inset-0 z-[80] bg-black lg:hidden">
            <LivePreviewPanel isMobile />
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx,.txt"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Link input modal */}
        <LinkInputModal
          isOpen={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          onAdd={handleAddLink}
        />
      </div>
    );
  }

  // Building Phase - Chat on left, live preview on right (split screen continues, responsive)
  return (
    <div className="fixed inset-0 z-[70] bg-black font-sans">
      <SpaceBackground />

      <div className="relative z-10 flex flex-col lg:flex-row h-full">
        {/* Left Side - Conversation with SuperJarvis (full width on mobile, half on desktop) */}
        <div className="flex-1 lg:w-1/2 flex flex-col lg:border-r border-cyan-400/20">
          {/* Header */}
          <header className="p-3 md:p-4 flex items-center border-b border-cyan-400/10">
            <button
              onClick={() => navigate('/apps')}
              className="p-2 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] backdrop-blur-3xl border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all hover:scale-105 active:scale-95 group mr-3 md:mr-4"
            >
              <ChevronLeft className="w-5 h-5 text-cyan-400 group-hover:text-cyan-200 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]" />
            </button>

            <button onClick={handleLogoClick} className="flex items-center space-x-2 md:space-x-3 hover:opacity-80 transition-opacity min-w-0">
              <AtomIcon className="w-7 h-7 md:w-8 md:h-8 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow shrink-0" />
              <h1 className="text-lg md:text-xl font-bold text-cyan-400 tracking-tight drop-shadow-[0_0_5px_rgba(34,211,238,0.5)] truncate font-sans">
                Building: {appName}
              </h1>
            </button>

            {/* Mobile: Preview Toggle Button */}
            <button
              onClick={() => setShowMobilePreview(true)}
              className="ml-auto p-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 lg:hidden"
            >
              <Smartphone className="w-5 h-5 text-cyan-400" />
            </button>
          </header>

          {/* Summary Bar - Clickable to edit */}
          <div className="p-3 border-b border-cyan-400/10 bg-cyan-400/[0.02]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <SummaryItem label="Name" value={appName} onClick={handleEditField} />
              <SummaryItem label="Description" value={description} onClick={handleEditField} />
              <SummaryItem label="Instructions" value={instructions} onClick={handleEditField} />
              <SummaryItem label="Goal" value={goal} onClick={handleEditField} />
            </div>

            {/* Knowledge items */}
            {knowledge.length > 0 && (
              <div className="mt-2 pt-2 border-t border-cyan-400/10">
                <button
                  onClick={handleEditField}
                  className="flex items-start space-x-2 text-left group hover:bg-cyan-400/5 p-2 rounded-lg transition-all -mx-2 w-full"
                >
                  <span className="text-cyan-400/50 text-xs font-semibold uppercase w-20 md:w-24 pt-0.5 shrink-0">Knowledge:</span>
                  <div className="flex-1 flex flex-wrap gap-1">
                    {knowledge.slice(0, 3).map(item => (
                      <span key={item.id} className="text-cyan-300 text-xs bg-cyan-400/10 px-2 py-0.5 rounded">
                        {item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name}
                      </span>
                    ))}
                    {knowledge.length > 3 && (
                      <span className="text-cyan-400/50 text-xs">+{knowledge.length - 3} more</span>
                    )}
                  </div>
                  <Edit className="w-3 h-3 text-cyan-400/30 group-hover:text-cyan-400 opacity-0 group-hover:opacity-100 transition-all shrink-0 mt-0.5" />
                </button>
              </div>
            )}
          </div>

          {/* Conversation Area - Chat with SuperJarvis */}
          <div
            ref={chatScrollRef}
            className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4"
          >
            {conversation.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center mr-3 shrink-0">
                    <AtomIcon className={`w-5 h-5 text-cyan-400 ${isBuilding ? 'animate-spin' : ''}`} />
                  </div>
                )}
                <div className={`max-w-[85%] md:max-w-[80%] p-3 md:p-4 rounded-2xl ${msg.role === 'user'
                  ? 'bg-cyan-400/20 border border-cyan-400/40'
                  : 'bg-cyan-500/[0.05] border border-cyan-400/20'
                  }`}>
                  <p className="text-cyan-300 text-sm leading-relaxed">{msg.message}</p>
                </div>
              </div>
            ))}

            {/* Build Status Indicator */}
            {isBuilding && buildStatus && (
              <div className="flex justify-start">
                <div className="flex items-center space-x-3 p-3 bg-cyan-400/10 border border-cyan-400/30 rounded-xl animate-pulse">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
                  <span className="text-cyan-400 text-sm">{buildStatus}</span>
                </div>
              </div>
            )}

            {/* Generated Code Preview (collapsible) */}
            {generatedCode.length > 0 && (
              <details className="mt-4">
                <summary className="text-cyan-400/60 text-xs cursor-pointer hover:text-cyan-400">
                  View generated code ({generatedCode.length} files)
                </summary>
                <div className="mt-2 p-3 bg-black/50 rounded-xl border border-cyan-400/20 max-h-40 overflow-auto">
                  <pre className="text-xs text-cyan-300/70 whitespace-pre-wrap">
                    {generatedCode.slice(-1)[0]?.substring(0, 500)}...
                  </pre>
                </div>
              </details>
            )}
          </div>

          {/* Chat Input with + button */}
          <div className="p-3 md:p-4 border-t border-cyan-400/10">
            <GlassCard className="flex items-center p-2 pr-3 md:pr-4 relative">
              {/* Plus button for adding knowledge */}
              <div className="relative">
                <button
                  onClick={() => setPlusMenuOpen(!plusMenuOpen)}
                  className={`p-2 md:p-3 rounded-full mr-2 md:mr-3 transition-all duration-300 border border-cyan-400/40 ${plusMenuOpen ? 'bg-cyan-400 rotate-45 text-black shadow-[0_0_35px_rgba(34,211,238,0.8)]' : 'bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.2)]'}`}
                >
                  <Plus className="w-5 h-5" />
                </button>
                <PlusMenu
                  isOpen={plusMenuOpen}
                  onClose={() => setPlusMenuOpen(false)}
                  onAddFile={handleAddFile}
                  onAddLink={() => setLinkModalOpen(true)}
                  onEditSettings={handleEditField}
                />
              </div>

              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Describe what to build or modify..."
                className="flex-1 bg-transparent border-none outline-none text-cyan-300 placeholder-cyan-400/40 text-sm md:text-base px-2 font-medium"
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />

              {/* Send arrow button */}
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim()}
                className={`p-2 md:p-2.5 rounded-full transition-all duration-300 border ${inputText.trim() ? 'bg-cyan-400 text-black border-cyan-400 shadow-[0_0_25px_rgba(34,211,238,0.6)] hover:shadow-[0_0_35px_rgba(34,211,238,0.8)] hover:scale-105' : 'bg-cyan-400/10 text-cyan-400/40 border-cyan-400/20 cursor-not-allowed'}`}
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </GlassCard>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Right Side - Live Preview (hidden on mobile, shown on lg+) */}
        <div className="hidden lg:flex lg:w-1/2">
          <LivePreviewPanel />
        </div>
      </div>

      {/* Mobile Preview Modal */}
      {showMobilePreview && (
        <div className="fixed inset-0 z-[80] bg-black lg:hidden">
          <LivePreviewPanel isMobile />
        </div>
      )}

      {/* Link input modal */}
      <LinkInputModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onAdd={handleAddLink}
      />
    </div>
  );
}
